import { Button, ConfigProvider, DatePicker, Input, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo } from 'react';
import {
  useGetStudySitesOptionsQuery,
  useGetStudyModalitiesOptionsQuery,
} from '@/state/slices/options';
import {
  useGetReferringPhysiciansQuery,
  useGetSonographersQuery,
} from '@/state/slices/reports';
import { useGetPhysicianOptionsQuery } from '@/state/slices/users';
import type { DatePreset } from '@/models/report';
import { CollapsibleSection } from '@/shared/ui/CollapsibleSection';
import styles from './ReportFilterRail.module.css';

interface ReportFilterRailProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  selectedSites: string[];
  onSitesChange: (sites: string[]) => void;
  selectedStudyTypes: string[];
  onStudyTypesChange: (types: string[]) => void;
  datePreset: DatePreset;
  onDatePresetChange: (preset: DatePreset) => void;
  customDateRange: [string, string] | null;
  onCustomDateRangeChange: (range: [string, string] | null) => void;
  selectedReferringPhysicians: string[];
  onReferringPhysiciansChange: (v: string[]) => void;
  selectedInterpretingPhysicians: string[];
  onInterpretingPhysiciansChange: (v: string[]) => void;
  selectedSonographers: string[];
  onSonographersChange: (v: string[]) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

const DATE_PRESETS: { label: string; value: DatePreset }[] = [
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: 'last7' },
  { label: 'Last 30 days', value: 'last30' },
  { label: 'Custom', value: 'custom' },
];

const DEFAULT_DATE_PRESET: DatePreset = 'last7';

const filterTheme = {
  components: {
    Input: { colorBgContainer: 'var(--sub-sidebar-surface-1)' },
    Select: { colorBgContainer: 'var(--sub-sidebar-surface-1)' },
    DatePicker: { colorBgContainer: 'var(--sub-sidebar-surface-1)' },
  },
};

export default function ReportFilterRail({
  searchText,
  onSearchChange,
  selectedSites,
  onSitesChange,
  selectedStudyTypes,
  onStudyTypesChange,
  datePreset,
  onDatePresetChange,
  customDateRange,
  onCustomDateRangeChange,
  selectedReferringPhysicians,
  onReferringPhysiciansChange,
  selectedInterpretingPhysicians,
  onInterpretingPhysiciansChange,
  selectedSonographers,
  onSonographersChange,
  hasActiveFilters,
  onClearFilters,
}: ReportFilterRailProps) {
  const { data: sites } = useGetStudySitesOptionsQuery();
  const { data: modalities } = useGetStudyModalitiesOptionsQuery();
  const { data: referringData } = useGetReferringPhysiciansQuery();
  const { data: sonographerData } = useGetSonographersQuery();
  const { data: physicianData } = useGetPhysicianOptionsQuery();

  const referringOptions = useMemo(
    () =>
      referringData?.results.map((p) => ({
        value: p.uuid,
        label: `${p.firstName} ${p.lastName}`,
      })) ?? [],
    [referringData]
  );

  const sonographerOptions = useMemo(
    () =>
      sonographerData?.results.map((p) => ({
        value: p.uuid,
        label: `${p.firstName} ${p.lastName}`,
      })) ?? [],
    [sonographerData]
  );

  const physicianOptions = useMemo(
    () =>
      physicianData?.results.map((p) => ({
        value: p.uuid,
        label: `${p.firstName} ${p.lastName}`,
      })) ?? [],
    [physicianData]
  );

  const scopeCount = selectedSites.length;
  const personnelCount =
    selectedReferringPhysicians.length +
    selectedInterpretingPhysicians.length +
    selectedSonographers.length;
  const dateActive = datePreset !== DEFAULT_DATE_PRESET;
  const studyDetailsCount = selectedStudyTypes.length + (dateActive ? 1 : 0);
  const handleRangePicker = (
    _dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: [string, string]
  ) => {
    if (dateStrings[0] && dateStrings[1]) {
      onCustomDateRangeChange(dateStrings);
    } else {
      onCustomDateRangeChange(null);
    }
  };

  const clearPersonnel = () => {
    onReferringPhysiciansChange([]);
    onInterpretingPhysiciansChange([]);
    onSonographersChange([]);
  };

  const clearStudyDetails = () => {
    onDatePresetChange(DEFAULT_DATE_PRESET);
    onCustomDateRangeChange(null);
    onStudyTypesChange([]);
  };

  return (
    <ConfigProvider theme={filterTheme}>
      <div className={styles.filterRail}>
        {/* ── Search (always visible) ──────────── */}
        <div className={styles.section}>
          <div className={styles.filterGroup}>
            <label htmlFor="report-search" className={styles.filterLabel}>
              Search
            </label>
            <Input
              id="report-search"
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search patient, MRN…"
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>

        {/* ── Scope (Site / Org) ───────────────── */}
        <CollapsibleSection
          title="Scope"
          count={scopeCount}
          onClear={() => onSitesChange([])}
        >
          <div className={styles.filterGroup}>
            <label htmlFor="report-site" className={styles.filterLabel}>
              Site / Org
            </label>
            <Select
              id="report-site"
              mode="multiple"
              allowClear
              placeholder="All sites"
              value={selectedSites}
              onChange={onSitesChange}
              options={sites?.map((s) => ({ value: s, label: s })) ?? []}
              className={styles.fullWidth}
            />
          </div>
        </CollapsibleSection>

        {/* ── Personnel ────────────────────────── */}
        <CollapsibleSection
          title="Personnel"
          count={personnelCount}
          onClear={clearPersonnel}
        >
          <div className={styles.filterGroup}>
            <label htmlFor="report-referring" className={styles.filterLabel}>
              Referring Physician
            </label>
            <Select
              id="report-referring"
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="All"
              value={selectedReferringPhysicians}
              onChange={onReferringPhysiciansChange}
              options={referringOptions}
              className={styles.fullWidth}
            />
          </div>
          <div className={styles.filterGroup}>
            <label htmlFor="report-interpreting" className={styles.filterLabel}>
              Interpreting Physician
            </label>
            <Select
              id="report-interpreting"
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="All"
              value={selectedInterpretingPhysicians}
              onChange={onInterpretingPhysiciansChange}
              options={physicianOptions}
              className={styles.fullWidth}
            />
          </div>
          <div className={styles.filterGroup}>
            <label htmlFor="report-sonographer" className={styles.filterLabel}>
              Sonographer / Tech
            </label>
            <Select
              id="report-sonographer"
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="All"
              value={selectedSonographers}
              onChange={onSonographersChange}
              options={sonographerOptions}
              className={styles.fullWidth}
            />
          </div>
        </CollapsibleSection>

        {/* ── Study Details (Date + Study type) ── */}
        <CollapsibleSection
          title="Study Details"
          count={studyDetailsCount}
          onClear={clearStudyDetails}
        >
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Date updated</span>
            <div className={styles.datePresetGrid}>
              {DATE_PRESETS.map(({ label, value }) => (
                <Button
                  key={value}
                  size="small"
                  type={datePreset === value ? 'primary' : 'default'}
                  className={styles.datePresetBtn}
                  onClick={() => onDatePresetChange(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
            {datePreset === 'custom' && (
              <DatePicker.RangePicker
                className={styles.fullWidth}
                value={
                  customDateRange
                    ? [dayjs(customDateRange[0]), dayjs(customDateRange[1])]
                    : null
                }
                onChange={handleRangePicker}
              />
            )}
          </div>
          <div className={styles.filterGroup}>
            <label htmlFor="report-study-type" className={styles.filterLabel}>
              Study type
            </label>
            <Select
              id="report-study-type"
              mode="multiple"
              allowClear
              placeholder="All types"
              value={selectedStudyTypes}
              onChange={onStudyTypesChange}
              options={modalities?.map((m) => ({ value: m, label: m })) ?? []}
              className={styles.fullWidth}
            />
          </div>
        </CollapsibleSection>

        {/* ── Footer ───────────────────────────── */}
        {hasActiveFilters && (
          <div className={styles.footer}>
            <Button
              block
              className={styles.footerButton}
              onClick={onClearFilters}
            >
              Clear All Filters
            </Button>
          </div>
        )}
      </div>
    </ConfigProvider>
  );
}
